import { FC } from 'react';
import { Box, Button, CircularProgress, TextField, useTheme } from '@mui/material';
import { genericTexts } from '@utils/translations/en';

interface AddGameFormProps {
  value: string;
  onChange: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
  isDuplicate: boolean;
  isLoading: boolean;
}

/**
 * Inline "Enter Game ID" form rendered below the sidepanel header.
 * The parent controls visibility and local state; this component is purely presentational.
 */
const AddGameForm: FC<AddGameFormProps> = ({
  value,
  onChange,
  onSave,
  onCancel,
  isDuplicate,
  isLoading,
}) => {
  const theme = useTheme();
  const borderColor = isDuplicate ? theme.palette.error.main : theme.palette.primary.main;

  return (
    <Box sx={{ mb: '0.75rem' }}>
      <TextField
        label="Enter Game ID"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        error={isDuplicate}
        helperText={isDuplicate ? 'Game ID already exists' : undefined}
        fullWidth
        size="medium"
        sx={{
          '& .MuiInputLabel-root.Mui-focused': { color: borderColor },
          '& .MuiOutlinedInput-root': {
            '& fieldset': { borderColor },
            '&:hover fieldset': { borderColor },
            '&.Mui-focused fieldset': { borderColor },
          },
        }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: '0.5rem' }}>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={!value || isDuplicate || isLoading}
          startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : undefined}
          sx={{
            borderRadius: '100px',
            textTransform: 'none',
            px: '1.5rem',
            background: theme.palette.primary.main,
            '&:hover': { background: theme.palette.primary.main },
          }}
        >
          {genericTexts.Save}
        </Button>
        <Button
          onClick={onCancel}
          sx={{ borderRadius: '100px', textTransform: 'none', color: theme.palette.primary.main }}
        >
          {genericTexts.Cancel}
        </Button>
      </Box>
    </Box>
  );
};

export default AddGameForm;
